/**
 * Config-style document templates API (new Settings UI).
 *
 * Distinct from the legacy markdown-template client in api/documents.ts —
 * those are kept as-is for back-compat. New UI reaches for this one.
 */
import client from "./client";

export type TemplateCategory =
  | "onboarding"
  | "compliance"
  | "policy"
  | "benefits"
  | "tax"
  | "other";

export type RenewalInterval = "annual" | "biannual" | "quarterly" | "custom";
export type EmploymentType =
  | "FULL_TIME"
  | "PART_TIME"
  | "CONTRACTOR"
  | "INTERN"
  | "TEMPORARY";

export type TemplateType = "file" | "form";

export interface DocumentTemplateConfig {
  id: string;
  org_id: string;
  name: string;
  type: string;
  description: string | null;
  category: TemplateCategory | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  is_required: boolean;
  requires_signature: boolean;
  deadline_days: number;
  renewal_interval: RenewalInterval | null;
  renewal_days: number | null;
  applies_to_all: boolean;
  applies_to_departments: string[] | null;
  applies_to_employment_types: EmploymentType[] | null;
  sort_order: number;
  is_active: boolean;
  /** ``file`` (default — PDF upload) or ``form`` (digital fillable form). */
  template_type: TemplateType;
  /** JSON array of field definitions when ``template_type === "form"``.
   *  Shape mirrors ``components/forms/types.ts:FormField``. */
  form_schema: Array<Record<string, unknown>> | null;
  created_at: string;
  updated_at: string;
}

export interface TemplateConfigCreatePayload {
  name: string;
  description?: string | null;
  category: TemplateCategory;
  is_required: boolean;
  requires_signature: boolean;
  deadline_days: number;
  renewal_interval?: RenewalInterval | null;
  renewal_days?: number | null;
  applies_to_all: boolean;
  applies_to_departments?: string[] | null;
  applies_to_employment_types?: EmploymentType[] | null;
  sort_order?: number;
  template_type?: TemplateType;
  form_schema?: Array<Record<string, unknown>> | null;
}

export async function listConfigTemplates(params?: {
  category?: string;
  includeInactive?: boolean;
}): Promise<DocumentTemplateConfig[]> {
  const query: Record<string, string> = {};
  if (params?.category) query.category = params.category;
  if (params?.includeInactive) query.include_inactive = "true";
  const res = await client.get<DocumentTemplateConfig[]>(
    "/document-templates",
    { params: query },
  );
  return res.data;
}

export async function listCategories(): Promise<
  { category: string; count: number }[]
> {
  const res = await client.get("/document-templates/categories");
  return res.data;
}

export async function createConfigTemplate(
  config: TemplateConfigCreatePayload,
  file?: File | null,
): Promise<DocumentTemplateConfig> {
  const form = new FormData();
  form.append("config", JSON.stringify(config));
  if (file) form.append("file", file);
  const res = await client.post<DocumentTemplateConfig>(
    "/document-templates",
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function updateConfigTemplate(
  id: string,
  patch: Partial<TemplateConfigCreatePayload & { is_active: boolean }>,
): Promise<DocumentTemplateConfig> {
  const res = await client.put<DocumentTemplateConfig>(
    `/document-templates/${id}`,
    patch,
  );
  return res.data;
}

export async function replaceTemplateFile(
  id: string,
  file: File,
): Promise<DocumentTemplateConfig> {
  const form = new FormData();
  form.append("file", file);
  const res = await client.post<DocumentTemplateConfig>(
    `/document-templates/${id}/file`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function deleteConfigTemplate(id: string): Promise<void> {
  await client.delete(`/document-templates/${id}`);
}

/**
 * Permanently delete an archived template.
 *
 * The backend refuses (HTTP 409) when there are still assignments
 * pointing at the template, unless ``force=true`` is passed (which
 * cascades the deletion to those assignments — destructive). Callers
 * should always show a clear "this can't be undone" confirm before
 * calling, especially with ``force=true``.
 */
export async function purgeConfigTemplate(
  id: string,
  opts?: { force?: boolean },
): Promise<void> {
  const params = opts?.force ? { force: true } : undefined;
  await client.delete(`/document-templates/${id}/permanent`, { params });
}

export async function reorderTemplates(
  items: { id: string; sort_order: number }[],
): Promise<{ updated: number }> {
  const res = await client.post("/document-templates/reorder", { items });
  return res.data;
}
